import React, { useEffect, useMemo, useState } from "react";
import "./ManagerDashboard.css";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getManagerSettings } from "../../functions/getManagerSettings";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { setBackButtonUrl } from "../uiSlice";
import { writeManagerDraft } from "../../lib/managerCardsSession";
import { swalTabooTheme } from "../../lib/swalTabooTheme";
import {
  TOPIC_CATEGORIES,
  MAX_ROUNDS_ALLOWED,
  MIN_POINTS,
  MAX_POINTS,
  MIN_TIMER,
  MAX_TIMER,
  aiToCardEntry,
  fetchAiCards,
  readLobbyNameFromSession,
} from "./managerHostShared";

const ShieldIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
    <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const FunIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <circle cx="9" cy="10" r="1" fill="currentColor" />
    <circle cx="15" cy="10" r="1" fill="currentColor" />
    <path d="M14 7h2" />
            </svg>
);

const WildIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
    <path d="M12 3c1 4 4 6 4 9a4 4 0 01-8 0c0-3 3-5 4-9z" />
            </svg>
);

const CATEGORY_ICONS = { safe: ShieldIcon, fun: FunIcon, wild: WildIcon };

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);

  const [topic, setTopic] = useState("");
  const [topicCategory, setTopicCategory] = useState("fun");
  const [timerMin, setTimerMin] = useState(1);
  const [maxPoints, setMaxPoints] = useState("100");
  const [loading, setLoading] = useState(false);
  const [cardCount, setCardCount] = useState("5");
  const [lobbyName, setLobbyName] = useState(readLobbyNameFromSession);
  const [themeName, setThemeName] = useState("guess_me");

  const storedUserData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const categoryMeta = useMemo(
    () => TOPIC_CATEGORIES.find((c) => c.id === topicCategory) || TOPIC_CATEGORIES[1],
    [topicCategory]
  );

  const customTopic = useMemo(() => String(topic || "").trim(), [topic]);

  const resolvedCardCount = useMemo(() => {
    const s = String(cardCount).trim();
    if (s === "") return null;
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    return Math.min(MAX_ROUNDS_ALLOWED, Math.max(1, Math.round(n)));
  }, [cardCount]);

  const resolvedMaxPoints = useMemo(() => {
    const s = String(maxPoints).trim();
    if (s === "") return null;
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    return Math.min(MAX_POINTS, Math.max(MIN_POINTS, Math.round(n)));
  }, [maxPoints]);

  const sliderStyle = useMemo(
    () => ({ "--md-progress": `${((timerMin - MIN_TIMER) / (MAX_TIMER - MIN_TIMER)) * 100}%` }),
    [timerMin]
  );

  const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, storedUserData]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/user"));
  }, [dispatch]);

  useEffect(() => {
    document.documentElement.classList.add("manager-static-bg");
    document.body.classList.add("manager-static-bg");
    return () => {
      document.documentElement.classList.remove("manager-static-bg");
      document.body.classList.remove("manager-static-bg");
    };
  }, []);

  useEffect(() => {
    if (!tokenForApi || !uid) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getManagerSettings({ token: tokenForApi, userId: uid, themeName });
        if (cancelled) return;
        const cachedGs = (() => {
          try {
            return JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
          } catch {
            return {};
          }
        })();
        const roundsVal = resp?.questions_no != null ? Number(resp.questions_no) : Number(cachedGs?.rounds);
        if (Number.isFinite(roundsVal) && roundsVal > 0) {
          setCardCount(String(Math.min(MAX_ROUNDS_ALLOWED, Math.round(roundsVal))));
        }
        const tVal = resp?.time_duration != null ? Number(resp.time_duration) : Number(cachedGs?.time);
        if (Number.isFinite(tVal) && tVal >= MIN_TIMER) {
          setTimerMin(Math.min(MAX_TIMER, Math.max(MIN_TIMER, tVal)));
        }
        const pVal = resp?.points != null ? Number(resp.points) : Number(cachedGs?.points ?? cachedGs?.maxPoints);
        if (Number.isFinite(pVal) && pVal >= MIN_POINTS) {
          setMaxPoints(String(Math.min(MAX_POINTS, Math.max(MIN_POINTS, Math.round(pVal)))));
        }
        if (resp?.lobby_name != null && String(resp.lobby_name).trim() !== "") {
          setLobbyName(String(resp.lobby_name).trim());
        } else {
          const fb = readLobbyNameFromSession();
          if (fb) setLobbyName(fb);
        }
        if (resp?.themename) setThemeName(String(resp.themename));
      } catch {
        if (!cancelled) {
          const fb = readLobbyNameFromSession();
          if (fb) setLobbyName((prev) => (prev && String(prev).trim() ? prev : fb));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenForApi, uid, themeName]);

  const persistGameSettings = () => {
    try {
      const prev = JSON.parse(sessionStorage.getItem("gameSettings") || "{}") || {};
      sessionStorage.setItem(
        "gameSettings",
        JSON.stringify({
          ...prev,
          rounds: resolvedCardCount ?? undefined,
          questions: resolvedCardCount ?? undefined,
          time: timerMin,
          time_duration: timerMin,
          points: resolvedMaxPoints ?? undefined,
          maxPoints: resolvedMaxPoints ?? undefined,
          topicCategory,
          topic: customTopic,
        })
      );
      } catch {
        // ignore
    }
  };

  const decreaseCards = () => {
    const base = resolvedCardCount ?? 2;
    setCardCount(String(Math.max(1, base - 1)));
  };

  const increaseCards = () => {
    const base = resolvedCardCount ?? 0;
    setCardCount(String(Math.min(MAX_ROUNDS_ALLOWED, base + 1)));
  };

  const handleCardsInput = (raw) => {
    const s = String(raw);
    if (s === "") {
      setCardCount("");
      return;
    }
    if (!/^\d+$/.test(s)) return;
    const n = Number(s);
    if (n > MAX_ROUNDS_ALLOWED) {
      setCardCount(String(MAX_ROUNDS_ALLOWED));
      return;
    }
    setCardCount(s);
  };

  const handleCardsBlur = () => {
    const s = String(cardCount).trim();
    if (s === "") return;
    const n = Number(s);
    if (!Number.isFinite(n)) {
      setCardCount("");
      return;
    }
    setCardCount(String(Math.min(MAX_ROUNDS_ALLOWED, Math.max(1, Math.round(n)))));
  };

  const decreasePoints = () => {
    const base = resolvedMaxPoints ?? MIN_POINTS + 1;
    setMaxPoints(String(Math.max(MIN_POINTS, base - 1)));
  };

  const increasePoints = () => {
    const base = resolvedMaxPoints ?? MIN_POINTS - 1;
    setMaxPoints(String(Math.min(MAX_POINTS, base + 1)));
  };

  const handlePointsInput = (raw) => {
    const s = String(raw);
    if (s === "") {
      setMaxPoints("");
      return;
    }
    if (!/^\d+$/.test(s)) return;
    const n = Number(s);
    if (n > MAX_POINTS) {
      setMaxPoints(String(MAX_POINTS));
      return;
    }
    setMaxPoints(s);
  };

  const handlePointsBlur = () => {
    const s = String(maxPoints).trim();
    if (s === "") return;
    const n = Number(s);
    if (!Number.isFinite(n)) {
      setMaxPoints("");
      return;
    }
    setMaxPoints(String(Math.min(MAX_POINTS, Math.max(MIN_POINTS, Math.round(n)))));
  };

  const handleGenerate = async () => {
    if (!customTopic) {
      await Swal.fire(
        swalTabooTheme({
          title: "Topic required",
          text: "Enter a topic before generating cards with AI.",
          icon: "warning",
        })
      );
      return;
    }

    if (resolvedCardCount == null) {
      await Swal.fire(
        swalTabooTheme({
          title: "Number of cards required",
          text: `Enter how many cards to generate (1 to ${MAX_ROUNDS_ALLOWED}).`,
          icon: "warning",
        })
      );
      return;
    }

    if (resolvedMaxPoints == null) {
      await Swal.fire(
        swalTabooTheme({
          title: "Points required",
          text: `Enter max points from ${MIN_POINTS} to ${MAX_POINTS}.`,
          icon: "warning",
        })
      );
      return;
    }

    const count = resolvedCardCount;
    setLoading(true);
    persistGameSettings();

    Swal.fire({
      ...swalTabooTheme(),
      title: "Generating cards…",
      html: `Creating <b>${count}</b> cards for <b>${customTopic}</b>…`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const allGenerated = await fetchAiCards({
        topic: customTopic,
        categoryTone: categoryMeta.tone,
        count,
        excludeWords: [],
        literalTopic: true,
        token: tokenForApi,
      });
      const cards = allGenerated.slice(0, count).map(aiToCardEntry);

      writeManagerDraft({
        topic: customTopic,
        topicCategory,
        timerMin,
        maxPoints: resolvedMaxPoints,
        cardCount: count,
        cards,
        cardIndex: 0,
        regenBaseCount: count,
        lobbyName,
        themeName,
      });

      Swal.close();
      navigate("/manager-cards", { replace: true });
    } catch (e) {
      Swal.close();
      await Swal.fire(
        swalTabooTheme({
          title: "Error",
          text: e?.message || "Failed to generate cards.",
          icon: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="md-page md-page--center">
      <div className="md-confetti" aria-hidden="true" />
      <div className="md-inner md-inner--center">
        <div className="md-panels">
          <section className="md-panel md-panel--topic" aria-label="Create with AI">
            <span className="md-panel-pill">Create with AI</span>
                <input
              className="md-topic-input"
                  value={topic}
              placeholder="Topic for AI (e.g. Animals, Sports…)"
                  onChange={(e) => setTopic(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
                />
            <div className="md-categories" role="listbox" aria-label="Topic tone">
              {TOPIC_CATEGORIES.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.id] || FunIcon;
                const active = topicCategory === cat.id;
                const lines = String(cat.subtitle || "").split("\n");
                return (
                  <button
                    key={cat.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`md-category-btn${active ? " md-category-btn--active" : ""}`}
                    onClick={() => setTopicCategory(cat.id)}
                  >
                    <span className="md-category-icon">
                      <Icon />
                    </span>
                    <span className="md-category-label">{cat.label}</span>
                    <span className="md-category-sub">
                      {lines.map((l, i) => (
                        <span key={i}>{l}</span>
                      ))}
                    </span>
                  </button>
                );
              })}
                  </div>
          </section>

          <section className="md-panel md-panel--settings" aria-label="Game settings">
            <div className="md-setting-block">
              <span className="md-setting-label">No. of cards</span>
              <div className="md-stepper md-stepper--cards">
                <button
                  type="button"
                  onClick={decreaseCards}
                  disabled={resolvedCardCount != null && resolvedCardCount <= 1}
                  aria-label="Fewer cards"
                >
                  −
                </button>
                <input
                  type="number"
                  className="md-stepper-input"
                  min={1}
                  max={MAX_ROUNDS_ALLOWED}
                  step={1}
                  value={cardCount}
                  onChange={(e) => handleCardsInput(e.target.value)}
                  onBlur={handleCardsBlur}
                  aria-label={`Number of cards from 1 to ${MAX_ROUNDS_ALLOWED}`}
                />
                <button
                  type="button"
                  onClick={increaseCards}
                  disabled={resolvedCardCount != null && resolvedCardCount >= MAX_ROUNDS_ALLOWED}
                  aria-label="More cards"
                >
                  +
                </button>
              </div>
            </div>

            <div className="md-setting-block">
              <span className="md-setting-label">Timer (Total Game)</span>
              <input
                type="range"
                className="md-timer-range"
                min={MIN_TIMER}
                max={MAX_TIMER}
                value={timerMin}
                style={sliderStyle}
                onChange={(e) => setTimerMin(Number(e.target.value))}
                aria-label="Total game timer in minutes"
              />
              <div className="md-timer-display">{timerMin} min{timerMin === 1 ? "" : "s"} total</div>
          </div>

            <div className="md-setting-block">
              <span className="md-setting-label">Points (max)</span>
              <div className="md-stepper md-stepper--points">
              <button
                type="button"
                  onClick={decreasePoints}
                  disabled={resolvedMaxPoints != null && resolvedMaxPoints <= MIN_POINTS}
                  aria-label="Lower max points"
                >
                  −
              </button>
                <input
                  type="number"
                  className="md-stepper-input"
                  min={MIN_POINTS}
                  max={MAX_POINTS}
                  step={1}
                  value={maxPoints}
                  onChange={(e) => handlePointsInput(e.target.value)}
                  onBlur={handlePointsBlur}
                  aria-label="Enter max points from 1 to 100"
                />
          <button
                  type="button"
                  onClick={increasePoints}
                  disabled={resolvedMaxPoints != null && resolvedMaxPoints >= MAX_POINTS}
                  aria-label="Raise max points"
                >
                  +
          </button>
              </div>
            </div>
          </section>
        </div>

        <div className="md-generate-wrap">
          <button
            type="button"
            className="md-generate-btn"
            onClick={() => void handleGenerate()}
            disabled={loading}
          >
            {loading ? "Generating…" : "Generate with AI"}
          </button>
        </div>
      </div>
    </div>
  );
}
